import { Component, inject, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductResume } from '../../product/product.types';
import { ProductComponent } from '../../product/product.component';
import { ShopComponent } from '../shop.component';
import { ShopService } from '../shop.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs';

@Component({
  selector: 'app-shop-or-product',
  imports: [ProductComponent, ShopComponent],
  templateUrl: './shop-or-product.component.html',
})
export class ShopOrProductComponent {

  #activatedRoute = inject(ActivatedRoute);
  #router = inject(Router);
  #shopService = inject(ShopService);

  #data = null;

  componentTypes = {
    shop: 'shop',
    product: 'product'
  };

  componentType: WritableSignal<string | null> = signal(null);

  #gotoProductDetail$ = toObservable(this.#shopService.gotoProductDetail);

  #currentUrl = '';

  constructor() {
    this.#getProductsResolverData()
    this.#setUpProductNavigation();
    this.#setUpPdpToPdpNavigation();
  }

  /**
   * PDP → PDP navigation (e.g. picking a product from the search-bar dropdown) only changes
   * route params, so Angular reuses this component AND the <app-product> inside it. The
   * refetch itself is handled in ProductComponent.handleRouteChanges, but reusing the
   * instance leaves local state pointing at the previous product: `selectedAttribute` is a
   * component-level signal, and the image carousel is an imperative KeenSlider built in
   * ngAfterViewInit, so neither reacts to the new product.
   *
   * Force a fresh mount by nullifying the type when the URL actually changes.
   * NOTE: re-assert via setTimeout (macrotask), NOT queueMicrotask. Microtasks flush BEFORE
   * Angular renders, so null → product would collapse into a no-op and the component would
   * never actually be destroyed. setTimeout lets change detection render the `null` state
   * (destroy) first, then recreate it fresh. Ported from pp/pfw.
   */
  #setUpPdpToPdpNavigation() {
    this.#activatedRoute.params.subscribe((params: any) => {
      const url = [params['category'], params['subcategory'], params['slug']].join('/');

      // First emission just records the landing URL — nothing to remount yet.
      const urlChanged = !!this.#currentUrl && url !== this.#currentUrl;
      this.#currentUrl = url;
      if (!urlChanged) return;

      const nextType = this.#getItemType(url);
      const wasShowingProduct = this.componentType() === this.componentTypes.product;

      if (wasShowingProduct && nextType === this.componentTypes.product) {
        this.componentType.set(null);
        setTimeout(() => this.componentType.set(nextType));
        return;
      }

      this.componentType.set(nextType);
    });
  }

  #getProductsResolverData() {
    this.#activatedRoute.snapshot.data['data'].subscribe((data: any) => {
      this.#data = data;
      this.#validateComponent()
    });
  }

  #validateComponent() {
    const slug = this.#activatedRoute.snapshot.paramMap.get('slug');

    if (!slug) {
      this.#router.navigate(['/shop']);
      return;
    }

    const category = this.#activatedRoute.snapshot.paramMap.get('category');
    const subcategory = this.#activatedRoute.snapshot.paramMap.get('subcategory');

    const url = [category, subcategory, slug].join('/')
    this.componentType.set(this.#getItemType(url));
  }

  #getItemType(slug: string) {
    const d: any = this.#data;
    if (!d) return this.componentTypes.shop;

    const product = d.data.products.find((p: ProductResume) => p.productUrl === slug);

    if (product) return this.componentTypes.product
    return this.componentTypes.shop;
  }

  #setUpProductNavigation() {
    this.#gotoProductDetail$.pipe(
      filter((url: string) => !!url),
      tap((url: string) => this.componentType.set(this.#getItemType(url)))
    ).subscribe()
  }
}
